import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import { maskPhone } from '../utils/formatters';

interface Props {
  onClose: () => void;
  onSave: () => void;
}

const RecruiterCreateModal: React.FC<Props> = ({ onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmed: true,
    name: '',
    position: '',
    telephone: '',
  });

  const generatePassword = () => {
    // Gerar senha aleatória de 12 caracteres usando base64url
    const array = new Uint8Array(8);
    crypto.getRandomValues(array);
    const password = btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    setFormData({ ...formData, password });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = maskPhone(e.target.value);
    setFormData({ ...formData, telephone: formatted });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      const dataToSend = {
        ...formData,
        telephone: formData.telephone.replace(/\D/g, '')
      };
      await api.post('/recruiter-list', dataToSend);
      toast.success('Recrutador criado com sucesso!');
      onSave();
    } catch (error) {
      toast.error('Erro ao criar recrutador');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Novo Recruiter</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Conta</h3>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>E-mail:</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Senha:</label>
              <div style={styles.passwordGroup}>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  style={styles.input}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={generatePassword}
                  style={styles.generateBtn}
                >
                  GERAR SENHA
                </button>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.confirmed}
                  onChange={(e) => setFormData({ ...formData, confirmed: e.target.checked })}
                  style={styles.checkbox}
                />
                Confirmado
              </label>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Perfil</h3>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Nome:</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Posição:</label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Telefone:</label>
              <input
                type="text"
                value={formData.telephone}
                onChange={handlePhoneChange}
                style={styles.input}
                placeholder="(00) 00000-0000"
                maxLength={15}
              />
            </div>
          </div>

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>
              Cancelar
            </button>
            <button type="submit" disabled={saving} style={styles.saveBtn}>
              {saving ? 'Criando...' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  form: {
    padding: '1.5rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  section: {
    marginBottom: '2rem',
    paddingBottom: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  sectionTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1.2rem',
    color: colors.primary,
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: colors.text,
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  passwordGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  generateBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    alignSelf: 'flex-start',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    color: colors.text,
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '2rem',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  saveBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
};

export default RecruiterCreateModal;
